// shadcn-style range slider built on Radix's Slider primitive.
//
// The codebase already pulls Radix primitives through the `radix-ui`
// umbrella package (see ./dialog.tsx, ./popover.tsx etc.) so no new
// dependency is needed — `Slider` is one of the exports of `radix-ui`.
//
// Supports both single-handle (one `value`) and dual-handle (`value =
// [min, max]`) modes; the component renders one Thumb per value entry.
// Visual language mirrors the rest of the design system: 6px-tall track
// in `bg-muted`, indigo Range fill, white-background thumb with a thin
// ring shadow + a 4px focus ring.
//
// API mirrors shadcn's published slider snippet so callers don't need to
// learn anything new.

import * as React from "react";
import { Slider as SliderPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root>) {
  // Determine how many thumbs to render. When `value` is controlled use
  // its length; otherwise fall back to `defaultValue.length`; finally
  // default to a single-handle slider running from `min` to `max`.
  const values = React.useMemo<number[]>(() => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(defaultValue)) return defaultValue;
    return [min, max];
  }, [value, defaultValue, min, max]);

  // exactOptionalPropertyTypes is on, so we can't pass `undefined` to
  // Radix Slider's `value`/`defaultValue` (their declared types are
  // `number[]`, not `number[] | undefined`). Spread the props
  // conditionally instead.
  return (
    <SliderPrimitive.Root
      data-slot="slider"
      {...(defaultValue !== undefined ? { defaultValue } : {})}
      {...(value !== undefined ? { value } : {})}
      min={min}
      max={max}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          "relative grow overflow-hidden rounded-full bg-muted data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5",
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn(
            "absolute bg-primary data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full",
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          className="block size-4 shrink-0 rounded-full border border-primary bg-card shadow-[var(--shadow-soft)] ring-ring/40 transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
